import { supabase } from './supabase';

/**
 * Cached access to the current user.
 *
 * Every supabase.auth call — getSession() included — acquires the same
 * exclusive auth lock, and on a fresh page load Supabase refreshes the token
 * while holding it. A page that mounts a hundred hooks queues a hundred lock
 * acquisitions behind that refresh; whichever is still waiting after the 10s
 * acquire timeout is aborted, surfacing as an unhandled
 * "AbortError: signal is aborted without reason".
 *
 * So ask Supabase once, keep the answer, and let a single auth listener keep
 * it current. Callers keep the same `await getCurrentUser()` signature.
 */

let cachedUser = null;
let hasResolved = false;
let inFlight = null;

// True once Supabase has said this tab arrived through a password-reset link.
// The event fires once, as soon as the link's token is read, and the token is
// then wiped from the address bar. The /reset-password page may mount after
// that, so it asks here rather than waiting for an event that already went by.
let recoverySeen = false;

// Every subscriber that wants to hear about sign-in, sign-out and token
// refresh. They are called from the single Supabase listener below rather than
// each registering one of their own.
const subscribers = new Set();

// ONE listener for the whole app.
//
// Registering a listener is not free. supabase-js runs this on every
// onAuthStateChange call (auth-js 2.95.3, dist/module/GoTrueClient.js:1617):
//
//   (async () => {
//     await this.initializePromise;
//     await this._acquireLock(this.lockAcquireTimeout, async () => {
//       this._emitInitialSession(id);
//     });
//   })();
//
// Fire and forget, with no .catch(). Each registration queues on the same
// exclusive auth lock, and any that is still waiting after lockAcquireTimeout
// is aborted — throwing an AbortError inside a promise nobody is holding. That
// is an unhandled rejection we cannot catch at the call site, which is why it
// reached Sentry as "signal is aborted without reason" from /profile.
//
// The profile page was registering one here, one per component using useAuth,
// one for useGuestGate and one in ReaderProfile itself. Collapsing them to this
// single registration removes both the queue and the uncatchable rejection.
supabase.auth.onAuthStateChange((event, session) => {
  cachedUser = session?.user ?? null;
  hasResolved = true;
  if (event === 'PASSWORD_RECOVERY') recoverySeen = true;
  subscribers.forEach(fn => {
    // One misbehaving subscriber must not stop the others being told.
    try { fn(event, session); } catch (err) {
      console.error('auth subscriber threw:', err?.message || err);
    }
  });
});

/**
 * Listen for auth changes. Same shape a caller would get from Supabase, minus
 * the cost of a second registration.
 *
 * Returns an unsubscribe function — call it from a useEffect cleanup.
 */
export function subscribeToAuth(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

/**
 * Returns the currently signed-in user, or null.
 *
 * After the first resolution this is synchronous in effect — no lock, no
 * network. Access is enforced by RLS server-side on every request, so the
 * cached user is sufficient for obtaining the current user id.
 */
export async function getCurrentUser() {
  if (hasResolved) return cachedUser;

  // Concurrent callers before the first resolution share one lookup rather
  // than each taking the lock.
  if (!inFlight) {
    inFlight = supabase.auth.getSession()
      .then(({ data: { session } }) => {
        cachedUser = session?.user ?? null;
        hasResolved = true;
        return cachedUser;
      })
      .catch(err => {
        // Without this a network failure and a signed-out user look identical.
        console.error('getCurrentUser: getSession failed:', err?.message || err);
        return null;
      })
      .finally(() => { inFlight = null; });
  }

  return inFlight;
}

/**
 * Whether this tab was opened from a password-reset link. Read by the
 * /reset-password page; see `recoverySeen` above.
 */
export function wasOpenedFromResetLink() {
  return recoverySeen;
}

/** Called once the new password is saved, so the flag cannot outlive the reset. */
export function clearResetLink() {
  recoverySeen = false;
}
